import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Textarea,
  Select,
  Option,
  Chip,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningRounded,
  AccountBalance as AccountIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import { getAccounts, updateAccount, deleteAccount, createAccount } from '../../main/API/account';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateApiErrorData, translateAxiosErrorMessage, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import { getPaginatedMeta } from '../../main/utils/pagination';

/** Show list/grid if user may list or only view accounts (read-only roles often get accounts_view). */
const ACCOUNTS_SCREEN_LIST_PERMISSIONS = [
  PERMISSIONS.ACCOUNTS_LIST,
  PERMISSIONS.ACCOUNTS_VIEW,
];

export default function AccountsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
  });
  
  // Notification states
  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    account_number: '',
    bank_name: '',
    is_active: true,
    note: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({
    name: '',
  });

  // Create modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    account_number: '',
    bank_name: '',
    is_active: true,
    note: '',
  });
  const [createFormErrors, setCreateFormErrors] = useState({
    name: '',
  });

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);

  // Validation function
  const validateAccountName = useCallback((name) => {
    if (!name || name.trim() === '') {
      return { valid: false, error: t('settingsPages.accounts.nameRequired') };
    }

    if (name.length > 200) {
      return { valid: false, error: t('settingsPages.accounts.nameMax200') };
    }

    return { valid: true };
  }, [t]);

  const loadAccounts = useCallback(async () => {
    if (!hasAnyPermission(ACCOUNTS_SCREEN_LIST_PERMISSIONS)) {
      setAccounts([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      // Remove empty filter values
      if (!params.search) delete params.search;
      if (params.is_active === '') delete params.is_active;
      else if (params.is_active !== undefined) {
        params.is_active = params.is_active === 'true' || params.is_active === true;
      }
      
      const response = await getAccounts(params);
      if (response && response.data) {
        const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
        setData(meta);
        setAccounts(meta.results);
      } else {
        setAccounts([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      setResponseMessage(error.message || t('settingsPages.accounts.loadFailed'));
      setFailedNotification(true);
      setAccounts([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasAnyPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadAccounts();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  // Create handlers
  const handleCreateClick = () => {
    setCreateForm({
      name: '',
      account_number: '',
      bank_name: '',
      is_active: true,
      note: '',
    });
    setCreateFormErrors({ name: '' });
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!hasPermission(PERMISSIONS.ACCOUNTS_CREATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    // Clear previous errors
    setCreateFormErrors({ name: '' });

    // Validate name before API call
    const nameValidation = validateAccountName(createForm.name);
    if (!nameValidation.valid) {
      setCreateFormErrors({ name: nameValidation.error });
      return;
    }

    setLoading(true);
    try {
      await createAccount(createForm);
      
      setResponseMessage(t('settingsPages.accounts.createdSuccess'));
      setSuccessNotification(true);
      setCreateModalOpen(false);
      setCreateForm({
        name: '',
        account_number: '',
        bank_name: '',
        is_active: true,
        note: '',
      });
      loadAccounts();
    } catch (error) {
      console.error('Failed to create account:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const fallback = t('settingsPages.accounts.saveFailed');
        if (errorData && errorData.name) {
          const nameError = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
          setCreateFormErrors({ name: translateAggregateBackendMessage(String(nameError), t) });
        }
        setResponseMessage(translateApiErrorData(errorData, t) || fallback);
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.accounts.saveFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = useCallback((account) => {
    setEditingAccount(account);
    setEditForm({
      name: account.name || '',
      account_number: account.account_number || '',
      bank_name: account.bank_name || '',
      is_active: account.is_active !== undefined ? account.is_active : true,
      note: account.note || '',
    });
    setEditFormErrors({ name: '' });
    setEditModalOpen(true);
  }, []);

  const handleEditSubmit = async () => {
    if (!editingAccount) return;
    if (!hasPermission(PERMISSIONS.ACCOUNTS_UPDATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }

    // Clear previous errors
    setEditFormErrors({ name: '' });

    // Prevent name changes for system accounts
    if (editingAccount.is_system_account && editForm.name !== editingAccount.name) {
      setEditFormErrors({ name: t('settingsPages.accounts.systemNameLocked') });
      setResponseMessage(t('settingsPages.accounts.systemNameLocked'));
      setFailedNotification(true);
      return;
    }

    // Validate name before API call
    const nameValidation = validateAccountName(editForm.name);
    if (!nameValidation.valid) {
      setEditFormErrors({ name: nameValidation.error });
      return;
    }

    setLoading(true);
    try {
      // For system accounts, exclude name from the update payload
      const updateData = editingAccount.is_system_account
        ? { ...editForm, name: editingAccount.name } // Keep original name
        : editForm;
      
      await updateAccount(editingAccount.id, updateData);
      
      setResponseMessage(t('settingsPages.accounts.updatedSuccess'));
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (error) {
      console.error('Failed to update account:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const fallback = t('settingsPages.accounts.saveFailed');
        if (errorData && errorData.message) {
          const message = errorData.message;
          if (typeof message === 'string' && message.toLowerCase().includes('name') && message.toLowerCase().includes('system account')) {
            setEditFormErrors({ name: translateAggregateBackendMessage(message, t) });
          } else if (errorData && errorData.name) {
            const nameError = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
            setEditFormErrors({ name: translateAggregateBackendMessage(String(nameError), t) });
          }
        } else if (errorData && errorData.name) {
          const nameError = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
          setEditFormErrors({ name: translateAggregateBackendMessage(String(nameError), t) });
        }
        setResponseMessage(translateApiErrorData(errorData, t) || fallback);
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.accounts.saveFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = useCallback((account) => {
    // Prevent deletion of system accounts
    if (account.is_system_account) {
      setResponseMessage(t('settingsPages.accounts.systemDeleteBlocked'));
      setFailedNotification(true);
      return;
    }
    setDeletingAccount(account);
    setDeleteModalOpen(true);
  }, [t]);

  const handleDeleteConfirm = async () => {
    if (!deletingAccount) return;
    if (!hasPermission(PERMISSIONS.ACCOUNTS_DELETE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    // Double-check: prevent deletion of system accounts
    if (deletingAccount.is_system_account) {
      setResponseMessage(t('settingsPages.accounts.systemDeleteBlocked'));
      setFailedNotification(true);
      setDeleteModalOpen(false);
      setDeletingAccount(null);
      return;
    }
    
    setLoading(true);
    try {
      await deleteAccount(deletingAccount.id);
      
      setResponseMessage(t('settingsPages.accounts.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingAccount(null);
      loadAccounts();
    } catch (error) {
      console.error('Failed to delete account:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.accounts.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = useCallback(
    (dateString) => {
      if (!dateString) return t('actions.na');
      try {
        const d = new Date(dateString);
        const time = d.toLocaleTimeString('en-US', {
          timeZone: 'Asia/Muscat',
          hour12: true,
          hour: 'numeric',
          minute: 'numeric',
        });
        return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${time}`;
      } catch (e) {
        return dateString;
      }
    },
    [t]
  );

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('settingsPages.accounts.colId'), flex: 1, minWidth: 80 },
      {
        field: 'name',
        headerName: t('settingsPages.accounts.colName'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="body-sm">{params.row.name}</Typography>
            {params.row.is_system_account && (
              <Tooltip title={`${t('settingsPages.accounts.systemAccount')} — ${t('settingsPages.accounts.cannotDelete')}`}>
                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<SecurityIcon fontSize="small" />}
                >
                  {t('settingsPages.accounts.colSystem')}
                </Chip>
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'account_number',
        headerName: t('settingsPages.accounts.colNumber'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => params.row.account_number || t('actions.na'),
      },
      {
        field: 'bank_name',
        headerName: t('paymentsScreen.colAccountName'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => params.row.bank_name || t('actions.na'),
      },
      {
        field: 'is_active',
        headerName: t('settingsPages.permissions.colStatus'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <Typography
            level="body-sm"
            color={params.row.is_active ? 'success' : 'neutral'}
          >
            {params.row.is_active
              ? t('settingsPages.permissions.statusActive')
              : t('settingsPages.permissions.statusInactive')}
          </Typography>
        ),
      },
      {
        field: 'is_system_account',
        headerName: t('settingsPages.accounts.colSystem'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) =>
          params.row.is_system_account ? (
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<SecurityIcon fontSize="small" />}
            >
              {t('settingsPages.accounts.systemAccount')}
            </Chip>
          ) : (
            <Typography level="body-sm" color="neutral">
              {t('settingsPages.accounts.systemNo')}
            </Typography>
          ),
      },
      {
        field: 'note',
        headerName: t('actions.notes'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => params.row.note || t('actions.na'),
      },
      {
        field: 'created_at',
        headerName: t('trackingEvent.colCreatedAt'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => formatDate(params.row.created_at),
      },
      {
        field: 'modified_at',
        headerName: t('trackingEvent.colModifiedAt'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => formatDate(params.row.modified_at),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.accounts.colActions'),
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          const isSystemAccount = params.row.is_system_account;
          const canDelete = hasPermission(PERMISSIONS.ACCOUNTS_DELETE) && !isSystemAccount;

          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {hasPermission(PERMISSIONS.ACCOUNTS_UPDATE) && (
                <Tooltip title={t('settingsPages.accounts.tooltipEdit')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(params.row);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete ? (
                <Tooltip title={t('settingsPages.accounts.tooltipDelete')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(params.row);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : hasPermission(PERMISSIONS.ACCOUNTS_DELETE) && isSystemAccount ? (
                <Tooltip title={t('settingsPages.accounts.systemDeleteBlocked')}>
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="neutral"
                      disabled
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [t, hasPermission, formatDate, handleEditClick, handleDeleteClick]
  );

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>

          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.accounts.breadcrumbSettings')}
                </Link>
                <Link color="neutral" href="/settings">
                  {t('settingsPages.accounts.breadcrumbMaster')}
                </Link>
                <Typography level="body-md">{t('settingsPages.accounts.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography textAlign={"start"} level="h1">{t('settingsPages.accounts.pageTitle')}</Typography>
                {hasPermission(PERMISSIONS.ACCOUNTS_CREATE) && (
                  <Button
                    startDecorator={<AccountIcon />}
                    onClick={handleCreateClick}
                    color="primary"
                    variant="solid"
                  >
                    {t('settingsPages.accounts.btnAdd')}
                  </Button>
                )}
              </Box>
            </Box>

            <PermissionGate permissions={ACCOUNTS_SCREEN_LIST_PERMISSIONS}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Input
                    placeholder={t('settingsPages.accounts.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Select
                    placeholder={t('actions.status')}
                    value={filters.is_active}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_active: value || '' }))}
                  >
                    <Option value="">{t('actions.all')}</Option>
                    <Option value="true">{t('settingsPages.permissions.statusActive')}</Option>
                    <Option value="false">{t('settingsPages.permissions.statusInactive')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={accounts || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? (data.count || accounts.length) : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            {/* Create Modal */}
            <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('settingsPages.accounts.modalCreateTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.name} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.accounts.labelName')} *</FormLabel>
                          <Input
                            value={createForm.name}
                            onChange={(e) => {
                              setCreateForm(prev => ({ ...prev, name: e.target.value }));
                              if (createFormErrors.name) {
                                setCreateFormErrors({ name: '' });
                              }
                            }}
                            error={!!createFormErrors.name}
                            size="md"
                          />
                          {createFormErrors.name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.name}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.accounts.labelNumber')}</FormLabel>
                          <Input
                            value={createForm.account_number}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, account_number: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('paymentsScreen.colAccountName')}</FormLabel>
                          <Input
                            value={createForm.bank_name}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, bank_name: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.status')}</FormLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <NativeCheckbox
                              checked={createForm.is_active}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                              label={t('settingsPages.permissions.labelActive')}
                              size="md"
                            />
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.notes')}</FormLabel>
                          <Textarea
                            value={createForm.note}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, note: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setCreateModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permissions={[PERMISSIONS.ACCOUNTS_CREATE]}>
                    <Button 
                      onClick={handleCreateSubmit} 
                      loading={loading}
                      fullWidth={{ xs: true, sm: false }}
                    >
                      {t('trackingEvent.createButton')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Edit Modal */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('settingsPages.accounts.modalEditTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    {editingAccount && editingAccount.is_system_account && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '8px',
                          backgroundColor: 'primary.50',
                          border: '1px solid',
                          borderColor: 'primary.200',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <SecurityIcon color="primary" fontSize="small" />
                          <Typography level="body-sm" fontWeight="md" color="primary">
                            {t('settingsPages.accounts.systemAccount')}
                          </Typography>
                        </Box>
                        <Typography level="body-sm" color="neutral">
                          {`${t('settingsPages.accounts.cannotDelete')} ${t('settingsPages.accounts.systemNameLocked')}`}
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.name} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.accounts.labelName')} *</FormLabel>
                          <Input
                            value={editForm.name}
                            onChange={(e) => {
                              // Prevent changes for system accounts
                              if (editingAccount && editingAccount.is_system_account) {
                                return;
                              }
                              setEditForm(prev => ({ ...prev, name: e.target.value }));
                              if (editFormErrors.name) {
                                setEditFormErrors({ name: '' });
                              }
                            }}
                            error={!!editFormErrors.name}
                            disabled={editingAccount && editingAccount.is_system_account}
                            readOnly={editingAccount && editingAccount.is_system_account}
                            size="md"
                            sx={{
                              ...(editingAccount && editingAccount.is_system_account && {
                                backgroundColor: 'neutral.50',
                                cursor: 'not-allowed',
                              }),
                            }}
                          />
                          {editFormErrors.name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.name}
                            </Typography>
                          )}
                          {editingAccount && editingAccount.is_system_account && (
                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                              {t('settingsPages.accounts.systemNameLocked')}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.accounts.labelNumber')}</FormLabel>
                          <Input
                            value={editForm.account_number}
                            onChange={(e) => setEditForm(prev => ({ ...prev, account_number: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('paymentsScreen.colAccountName')}</FormLabel>
                          <Input
                            value={editForm.bank_name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, bank_name: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.status')}</FormLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <NativeCheckbox
                              checked={editForm.is_active}
                              onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                              label={t('settingsPages.permissions.labelActive')}
                              size="md"
                            />
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.notes')}</FormLabel>
                          <Textarea
                            value={editForm.note}
                            onChange={(e) => setEditForm(prev => ({ ...prev, note: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setEditModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permissions={[PERMISSIONS.ACCOUNTS_UPDATE]}>
                    <Button 
                      onClick={handleEditSubmit} 
                      loading={loading}
                      fullWidth={{ xs: true, sm: false }}
                    >
                      {t('actions.save')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRounded />
                  {t('settingsPages.accounts.deleteTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {deletingAccount && deletingAccount.is_system_account ? (
                    <Box>
                      <Typography level="body-md" color="danger" sx={{ mb: 1 }}>
                        {t('settingsPages.accounts.systemDeleteBlocked')}
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        {t('settingsPages.accounts.cannotDelete')}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography level="body-md">
                      {deletingAccount &&
                        t('settingsPages.accounts.deleteConfirm', { name: deletingAccount.name })}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
                    {deletingAccount && deletingAccount.is_system_account ? t('actions.close') : t('actions.cancel')}
                  </Button>
                  {(!deletingAccount || !deletingAccount.is_system_account) && (
                    <PermissionGate permissions={[PERMISSIONS.ACCOUNTS_DELETE]}>
                      <Button variant="solid" color="danger" onClick={handleDeleteConfirm} loading={loading}>
                        {t('actions.delete')}
                      </Button>
                    </PermissionGate>
                  )}
                </DialogActions>
              </ModalDialog>
            </Modal>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

